import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { getImageUrl } from "src/Common/common";
import { can, handleError } from "src/Common/function";
import RoleService from "src/Services/API/role.service";
import ConfirmDialog from "src/Common/confirmdialog";
import CreateRoleModal from "./CreateRoleModal";
import AttachPolicyModal from "./AttachPolicyModal";

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 2.83334C8.32217 2.83334 8.58334 3.09451 8.58334 3.41668V7.41668H12.5833C12.9055 7.41668 13.1667 7.67784 13.1667 8.00001C13.1667 8.32218 12.9055 8.58334 12.5833 8.58334H8.58334V12.5833C8.58334 12.9055 8.32217 13.1667 8 13.1667C7.67783 13.1667 7.41667 12.9055 7.41667 12.5833V8.58334H3.41667C3.0945 8.58334 2.83334 8.32218 2.83334 8.00001C2.83334 7.67784 3.0945 7.41668 3.41667 7.41668H7.41667V3.41668C7.41667 3.09451 7.67783 2.83334 8 2.83334Z"
      fill="#1A2332"
    />
  </svg>
);

const PolicyDocIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none">
    <path
      d="M12.5 1.875H5.83333C4.79779 1.875 3.95833 2.71446 3.95833 3.75V16.25C3.95833 17.2855 4.79779 18.125 5.83333 18.125H14.1667C15.2022 18.125 16.0417 17.2855 16.0417 16.25V5.41667L12.5 1.875ZM5.20833 3.75C5.20833 3.40482 5.48816 3.125 5.83333 3.125H11.875V5.625C11.875 6.31536 12.4346 6.875 13.125 6.875H14.7917V16.25C14.7917 16.5952 14.5118 16.875 14.1667 16.875H5.83333C5.48816 16.875 5.20833 16.5952 5.20833 16.25V3.75Z"
      fill="#0058E5"
    />
    <path
      d="M6.875 9.79167C6.875 9.44649 7.15482 9.16667 7.5 9.16667H12.5C12.8452 9.16667 13.125 9.44649 13.125 9.79167C13.125 10.1368 12.8452 10.4167 12.5 10.4167H7.5C7.15482 10.4167 6.875 10.1368 6.875 9.79167Z"
      fill="#0058E5"
    />
    <path
      d="M6.875 12.7083C6.875 12.3632 7.15482 12.0833 7.5 12.0833H12.5C12.8452 12.0833 13.125 12.3632 13.125 12.7083C13.125 13.0535 12.8452 13.3333 12.5 13.3333H7.5C7.15482 13.3333 6.875 13.0535 6.875 12.7083Z"
      fill="#0058E5"
    />
  </svg>
);

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 1.5C4.41015 1.5 1.5 4.41015 1.5 8C1.5 11.5899 4.41015 14.5 8 14.5C11.5899 14.5 14.5 11.5899 14.5 8C14.5 4.41015 11.5899 1.5 8 1.5ZM0.5 8C0.5 3.85786 3.85786 0.5 8 0.5C12.1421 0.5 15.5 3.85786 15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8Z"
      fill="#0058E5"
    />
    <path
      d="M8 7C8.27614 7 8.5 7.22386 8.5 7.5V11.1667C8.5 11.4428 8.27614 11.6667 8 11.6667C7.72386 11.6667 7.5 11.4428 7.5 11.1667V7.5C7.5 7.22386 7.72386 7 8 7Z"
      fill="#0058E5"
    />
    <path
      d="M8.66667 5.16667C8.66667 5.53486 8.36819 5.83333 8 5.83333C7.63181 5.83333 7.33333 5.53486 7.33333 5.16667C7.33333 4.79848 7.63181 4.5 8 4.5C8.36819 4.5 8.66667 4.79848 8.66667 5.16667Z"
      fill="#0058E5"
    />
  </svg>
);

function StatCard({ label, value, icon, iconBg }) {
  return (
    <div className="flex-1 flex items-center justify-between gap-2 bg-white rounded-[12px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] px-5 py-6">
      <div className="flex flex-col items-start gap-0.5 overflow-hidden">
        <p className="text-[14px] leading-5 tracking-[0.14px] font-OpenSansSemiBold text-[#7C829A] capitalize">
          {label}
        </p>
        <p className="text-[24px] leading-8 font-OpenSansSemiBold text-[#1A2332] capitalize">{value}</p>
      </div>
      {icon && (
        <div
          className="w-10 h-10 shrink-0 flex items-center justify-center rounded-[11.43px]"
          style={{ background: iconBg }}
        >
          {icon}
        </div>
      )}
    </div>
  );
}

function branchInitials(name) {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/**
 * Role detail page — stats, tabbed content (Policies / Users / Activity Log),
 * a policy-card grid with attach/detach, and a Role Properties side panel
 * (inheritance, active branches, dynamic-scoping note). Mirrors the design.
 */
function RoleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [tab, setTab] = useState("policies");
  const [editShown, setEditShown] = useState(false);
  const [attachShown, setAttachShown] = useState(false);
  const [detachTarget, setDetachTarget] = useState(null);

  const load = () => {
    RoleService.getDetail(id)
      .then((res) => setRole(res.data))
      .catch(handleError);
  };

  useEffect(load, [id]);

  const detachPolicy = (policyId) => {
    setDetachTarget(policyId);
  };

  const confirmDetach = () => {
    const remaining = role.policies.filter((p) => p.id !== detachTarget).map((p) => p.id);
    RoleService.setPolicies(Number(id), remaining)
      .then(() => {
        toast("Policy detached");
        load();
      })
      .catch(handleError);
    setDetachTarget(null);
  };

  if (!role) {
    return <div className="min-h-screen bg-[#FBFCFF] px-7 py-10 text-[#868DA6] font-OpenSansRegular">Loading…</div>;
  }

  const policies = role.policies ?? [];
  const users = role.users ?? [];
  const activeBranches = role.activeBranches ?? [];

  return (
    <div className="min-h-screen bg-[#FBFCFF] px-7 py-6">
      {detachTarget !== null && (
        <ConfirmDialog
          title="DETACH POLICY"
          description="Are you sure you want to detach this policy from the role?"
          shown={detachTarget !== null}
          onConfirmClicked={confirmDetach}
          onCancelClicked={() => setDetachTarget(null)}
        />
      )}

      {/* History-based back: this page is reached from the role list AND from
          policy detail, so a hardcoded /roles would break the latter. */}
      <div className="flex items-center cursor-pointer mb-4" onClick={() => navigate(-1)}>
        <img src={getImageUrl("/images/common/back_arrow.png")} alt="" className="w-[24px]" />
        <span className="font-OpenSansRegular text-[14px] font-[700] text-subTitleColor">back</span>
      </div>

      <div className="flex items-start justify-between mb-5">
        <div className="flex flex-col items-start gap-1">
          <h1 className="text-[24px] leading-8 font-OpenSansSemiBold text-[#1A2332] capitalize">{role.name}</h1>
          <p className="max-w-[463px] text-[16px] leading-6 font-OpenSansRegular text-[#7C829A]">
            {role.description || "—"}
          </p>
          <div className="pt-2 flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
              <path
                d="M10 5.375C10 2.82068 7.92932 0.75 5.375 0.75C2.82068 0.75 0.75 2.82068 0.75 5.375C0.75 7.92932 2.82068 10 5.375 10C7.92932 10 10 7.92932 10 5.375ZM10.75 5.375C10.75 8.34353 8.34353 10.75 5.375 10.75C2.40647 10.75 0 8.34353 0 5.375C0 2.40647 2.40647 0 5.375 0C8.34353 0 10.75 2.40647 10.75 5.375Z"
                fill="#7C829A"
              />
              <path
                d="M5 2.375C5 2.16789 5.16789 2 5.375 2C5.58211 2 5.75 2.16789 5.75 2.375V5.14307L7.54248 6.03955C7.72766 6.13214 7.80292 6.35728 7.71045 6.54248C7.61786 6.72766 7.39272 6.80292 7.20752 6.71045L5.20752 5.71045C5.08048 5.64693 5 5.51704 5 5.375V2.375Z"
                fill="#7C829A"
              />
            </svg>
            <span className="text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansRegular text-[#7C829A]">
              Last updated: {role.updatedAt ? moment(role.updatedAt).fromNow() : "—"}
              {role.updatedBy?.name ? ` by ${role.updatedBy.name}` : ""}
            </span>
          </div>
        </div>
        {can("iam:updateRole") && (
          <button
            onClick={() => setEditShown(true)}
            className="h-8 px-3 rounded-[8px] bg-[#0065FD] text-white text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold hover:bg-[#0048bd] inline-flex items-center justify-center gap-2"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path
                  d="M12.4994 11.3332C12.7755 11.3332 12.9994 11.557 12.9994 11.8332C12.9994 12.1093 12.7755 12.3332 12.4994 12.3332H6.49938C6.22323 12.3332 5.99938 12.1093 5.99938 11.8332C5.99938 11.557 6.22323 11.3332 6.49938 11.3332H12.4994Z"
                  fill="white"
                />
                <path
                  d="M11.3327 1.91536C11.3327 1.6727 11.2361 1.43985 11.0645 1.26823C10.8929 1.0966 10.6601 1 10.4174 1C10.1747 1 9.94185 1.09661 9.77022 1.26823L1.76306 9.27539C1.664 9.3744 1.59138 9.49707 1.55213 9.63151L1.07817 11.2539L2.70121 10.7806C2.83572 10.7414 2.95891 10.6687 3.05798 10.5697L11.0645 2.5625C11.2361 2.39092 11.3327 2.158 11.3327 1.91536ZM12.3327 1.91536C12.3327 2.42322 12.1306 2.91042 11.7715 3.26953L3.76502 11.2767C3.54696 11.4947 3.27724 11.654 2.98116 11.7402L1.06645 12.2988C0.923129 12.3406 0.770948 12.3437 0.626344 12.3066C0.481734 12.2696 0.349769 12.1941 0.244183 12.0885C0.138548 11.9829 0.0631595 11.8504 0.0260841 11.7057C-0.010928 11.5611 -0.00856058 11.409 0.0332455 11.2656L0.591839 9.35091C0.678205 9.05516 0.838116 8.78617 1.05603 8.56836L9.06319 0.561198C9.42235 0.20204 9.90943 4.37772e-06 10.4174 0C10.9253 0 11.4124 0.202037 11.7715 0.561198C12.1306 0.92035 12.3327 1.40748 12.3327 1.91536Z"
                  fill="white"
                />
                <path
                  d="M8.14586 1.47964C8.34112 1.28438 8.65763 1.28438 8.85289 1.47964L10.8529 3.47964C11.0482 3.6749 11.0482 3.99141 10.8529 4.18667C10.6576 4.38193 10.3411 4.38193 10.1459 4.18667L8.14586 2.18667C7.9506 1.99141 7.9506 1.6749 8.14586 1.47964Z"
                  fill="white"
                />
              </svg>
            </span>
            Edit Role
          </button>
        )}
      </div>

      {/* Stats cards */}
      <div className="flex gap-4 mb-6">
        <StatCard
          label="Assigned Users"
          value={users.length}
          iconBg="#DAEBFF"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M13.333 17.5v-1.667a3.333 3.333 0 00-3.333-3.333H4.167a3.333 3.333 0 00-3.334 3.333V17.5M7.083 9.167a3.333 3.333 0 100-6.667 3.333 3.333 0 000 6.667zM19.167 17.5v-1.667a3.333 3.333 0 00-2.5-3.225M13.333 2.608a3.333 3.333 0 010 6.459"
                stroke="#0058E5"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
        <StatCard
          label="Attached Policies"
          value={policies.length}
          iconBg="#F3E8FF"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 1.042l7.083 3.125v4.166c0 4.34-2.917 8.4-7.083 9.375-4.167-.974-7.083-5.035-7.083-9.375V4.167L10 1.042z"
                stroke="#7E22CE"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
        <StatCard
          label="Active Branches"
          value={activeBranches.length}
          iconBg="#DCFCE7"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M16.667 5.833L7.5 15l-4.167-4.167"
                stroke="#22C55E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        />
      </div>

      <div className="flex gap-6">
        {/* Main column */}
        <div className="flex-1">
          {/* Tabs */}
          <div className="flex items-center justify-between border-b border-[#E5E8F3] mb-5">
            <div className="flex gap-6">
              {[
                ["policies", `Policies`],
                ["users", `Users (${users.length})`],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`pb-3 text-[14px] font-OpenSansSemiBold border-b-2 -mb-[1px] ${
                    tab === key
                      ? "text-[#0058E5] border-[#0058E5]"
                      : "text-[#868DA6] border-transparent hover:text-[#1A2332]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {tab === "policies" && can("iam:updateRole") && (
              <button
                onClick={() => setAttachShown(true)}
                className="mb-2 h-8 px-3 rounded-[8px] bg-white shadow-[0px_1px_2px_rgba(16,24,40,0.05)] outline outline-1 outline-offset-[-1px] outline-[#C9CEE2] text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332] hover:bg-[#F8F9FE] inline-flex items-center justify-center gap-2"
              >
                <PlusIcon />
                Attach Policy
              </button>
            )}
          </div>

          {/* Tab content */}
          {tab === "policies" && (
            <div className="bg-white rounded-[12px] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3] shadow-[0px_1px_2px_rgba(0,0,0,0.06)] overflow-hidden">
              {policies.length === 0 ? (
                <p className="text-[14px] font-OpenSansRegular text-[#868DA6] py-8 text-center">
                  No policies attached yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#F8F9FE] border-b border-[#E5E8F3]">
                        <th className="text-left px-5 py-3 text-[12px] leading-[18px] tracking-[0.5px] font-OpenSansBold text-[#5F6477] uppercase">
                          Policy
                        </th>
                        <th className="text-left px-5 py-3 text-[12px] leading-[18px] tracking-[0.5px] font-OpenSansBold text-[#5F6477] uppercase">
                          Permissions
                        </th>
                        <th className="text-left px-5 py-3 text-[12px] leading-[18px] tracking-[0.5px] font-OpenSansBold text-[#5F6477] uppercase">
                          Status
                        </th>
                        <th className="text-right px-5 py-3 text-[12px] leading-[18px] tracking-[0.5px] font-OpenSansBold text-[#5F6477] uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {policies.map((policy) => (
                        <tr
                          key={policy.id}
                          onClick={() => navigate(`/policies/${policy.id}`)}
                          className="border-b border-[#E5E8F3] last:border-b-0 hover:bg-[#F8F9FE] cursor-pointer"
                        >
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 shrink-0 rounded-[8px] bg-[#EAF3FF] flex items-center justify-center">
                                <PolicyDocIcon />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[14px] leading-5 font-OpenSansSemiBold text-[#1A2332] truncate">
                                  {policy.name}
                                </p>
                                <p className="text-[12px] leading-[18px] font-OpenSansRegular text-[#868DA6] truncate max-w-[360px]">
                                  {policy.description}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            <span className="text-[14px] leading-5 font-OpenSansRegular text-[#1A2332]">
                              {policy.permissionCount} Permissions
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <span className="inline-flex px-2 py-[2px] rounded-[6px] bg-[#E9F8EF] text-[#1A9E5A] text-[11px] font-OpenSansSemiBold">
                              Active
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-3">
                              {can("iam:updateRole") && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    detachPolicy(policy.id);
                                  }}
                                  className="text-[#D93A3A] text-[12px] font-OpenSansSemiBold hover:underline"
                                >
                                  Detach
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "users" && (
            <div className="bg-white rounded-[12px] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3] shadow-[0px_1px_2px_rgba(0,0,0,0.06)] overflow-hidden">
              {users.length === 0 ? (
                <p className="text-[14px] font-OpenSansRegular text-[#868DA6] py-8 text-center">
                  No users assigned to this role.
                </p>
              ) : (
                users.map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center gap-3 px-5 py-3 border-b border-[#E5E8F3] last:border-b-0"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#EAF3FF] flex items-center justify-center text-[#0058E5] text-[12px] font-OpenSansSemiBold">
                      {(u.name || u.email || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-[14px] font-OpenSansSemiBold text-[#1A2332]">{u.name || "—"}</p>
                      <p className="text-[12px] font-OpenSansRegular text-[#868DA6]">{u.email}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Side panel */}
        <div className="w-[300px] shrink-0 flex flex-col gap-4">
          <div className="bg-white rounded-[12px] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3] shadow-[0px_1px_2px_rgba(0,0,0,0.06)] p-5">
            <p className="text-[15px] font-OpenSansBold text-[#1A2332] mb-4">Role Properties</p>

            <p className="text-[11px] font-OpenSansSemiBold text-[#868DA6] uppercase tracking-[0.1px]">Created</p>
            <p className="text-[14px] font-OpenSansSemiBold text-[#1A2332] mt-1 mb-3">
              {role.createdAt ? moment(role.createdAt).format("MMM D, YYYY") : "—"}
            </p>

            <p className="text-[11px] font-OpenSansSemiBold text-[#868DA6] uppercase tracking-[0.1px]">Created By</p>
            <p className="text-[14px] font-OpenSansSemiBold text-[#1A2332] mt-1">{role.createdBy?.name || "—"}</p>
          </div>

          <div className="bg-white rounded-[12px] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3] shadow-[0px_1px_2px_rgba(0,0,0,0.06)] p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[15px] font-OpenSansBold text-[#1A2332]">Active Branches</p>
              <span className="text-[12px] font-OpenSansRegular text-[#868DA6]">{activeBranches.length} Total</span>
            </div>
            {activeBranches.length === 0 ? (
              <p className="text-[13px] font-OpenSansRegular text-[#868DA6]">No branches assigned.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {activeBranches.map((b) => (
                  <div key={b.id} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-[8px] bg-[#F0F1F5] flex items-center justify-center text-[#404A6E] text-[11px] font-OpenSansSemiBold">
                      {branchInitials(b.name)}
                    </div>
                    <span className="text-[14px] font-OpenSansRegular text-[#1A2332]">{b.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[#EAF3FF] rounded-[12px] outline outline-1 outline-offset-[-1px] outline-[#C8E1FF] p-5">
            <div className="flex items-start gap-3 overflow-hidden">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="shrink-0 opacity-80"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10 1.875C5.51269 1.875 1.875 5.51269 1.875 10C1.875 14.4873 5.51269 18.125 10 18.125C14.4873 18.125 18.125 14.4873 18.125 10C18.125 5.51269 14.4873 1.875 10 1.875ZM9.375 6.25C9.375 5.90482 9.65482 5.625 10 5.625C10.3452 5.625 10.625 5.90482 10.625 6.25C10.625 6.59518 10.3452 6.875 10 6.875C9.65482 6.875 9.375 6.59518 9.375 6.25ZM8.75 8.75C8.75 8.40482 9.02982 8.125 9.375 8.125H10C10.3452 8.125 10.625 8.40482 10.625 8.75V13.125H11.25C11.5952 13.125 11.875 13.4048 11.875 13.75C11.875 14.0952 11.5952 14.375 11.25 14.375H8.75C8.40482 14.375 8.125 14.0952 8.125 13.75C8.125 13.4048 8.40482 13.125 8.75 13.125H9.375V9.375H9.375C9.02982 9.375 8.75 9.09518 8.75 8.75Z"
                  fill="#0065FD"
                />
              </svg>
              <div className="flex-1 self-stretch flex flex-col items-start gap-1.5">
                <div className="self-stretch flex flex-col items-start gap-1">
                  <p className="text-[14px] leading-5 tracking-[0.14px] font-OpenSansSemiBold text-[#1A2332] capitalize">
                    Dynamic Scoping
                  </p>
                  <p className="self-stretch text-[12px] leading-[18px] tracking-[0.12px] font-OpenSansRegular text-[rgba(11,46,107,0.69)]">
                    This role is configured with "Assigned Branch" logic. Users will only see data for branches where
                    they are explicitly listed as a team member.
                  </p>
                </div>
                {/* <button
                  type="button"
                  className="h-8 px-1 rounded-[6px] inline-flex items-center justify-center gap-2 text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#0065FD] hover:underline"
                >
                  Learn more
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M5.86 3.53a.75.75 0 0 1 1.06 0l4 4a.75.75 0 0 1 0 1.06l-4 4a.75.75 0 0 1-1.06-1.06L9.34 8 5.86 4.53a.75.75 0 0 1 0-1Z"
                      fill="#0065FD"
                    />
                  </svg>
                </button> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreateRoleModal shown={editShown} role={role} onClose={() => setEditShown(false)} onSaved={load} />
      <AttachPolicyModal
        shown={attachShown}
        roleId={Number(id)}
        attachedPolicyIds={policies.map((p) => p.id)}
        onClose={() => setAttachShown(false)}
        onSaved={load}
      />
    </div>
  );
}

export default RoleDetail;
